import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../common/Config";
import CommonDatePicker from "../datepicker/CommonDatePicker";
import { format } from "date-fns";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Link as MuiLink,
  Skeleton,
  InputAdornment,
  Button,
  Grid2,
} from "@mui/material";
import { Iconify } from "src/index";
import "../common/commonStyles.css";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";
const columns = [
  { id: "date", label: "Order Date", numeric: false, width: 80 },
  { id: "reference_no", label: "Order ID", numeric: false, width: 70 },
  { id: "customer", label: "Customers (A/C No)", numeric: false, width: 150 },
  { id: "grand_total", label: "Order Amt", numeric: true, width: 100 },
  { id: "grand_total1", label: "Current Amt", numeric: true, width: 90 },
  { id: "route_number", label: "Route", numeric: false, width: 60 },
  { id: "picklist_number", label: "Picking No", numeric: false, width: 80 },
  { id: "manifest_id", label: "Manifest No", numeric: false, width: 90 },
  { id: "trip_number", label: "Trip No", numeric: false, width: 80 },
  { id: "tax_invoice_date", label: "Tax Inv Date", numeric: false, width: 100 },
  { id: "sale_status", label: "Status", numeric: false, width: 100 },
];

const getStatusChipProps = (status) => {
  const map = {
    Rejected: { color: "error" },
    Intransit: { color: "success" },
    Undelivered: { color: "warning" },
    Hold: { color: "warning" },
    New: { color: "info" },
    Completed: { color: "success" },
    Cancelled: { color: "error" },
  };
  return map[status] || { color: "default" };
};


const SalesOrderHistory = () => {
  const [salesDates, setSalesDates] = useState([null, null]);
  const [grandTotal, setGrandTotal] = useState("");
  const [currentGrandTotal, setCurrentGrandTotal] = useState("");
  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
    }
  }, [accessToken]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchDebounced(searchValue);
    }, 500);
    return () => clearTimeout(handle);
  }, [searchValue]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const orderColumnIndex = 0;
    (async () => {
      const settings = await getCompanySettings();
      if (!active) {
        return;
      }
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      let reqConfig = {
        headers: {
          authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
      };
      let payload;
      if (useEncrypt) {
        const plain = {
          draw: String(page + 1),
          start: String(page * rowsPerPage),
          length: String(rowsPerPage),
          searchValue: searchDebounced,
          search: { value: searchDebounced, regex: "false" },
          searchByFromdate: startDate,
          searchByTodate: endDate,
          authorization: `Bearer ${token}`,
          order: [{ column: String(orderColumnIndex), dir: "desc" }],
          columns: columns.map((column) => ({
            data: column.id,
            name: "",
            searchable: "true",
            orderable: "true",
            search: { value: "", regex: "false" },
          })),
        };
        const ep = encryptPayload(plain);
        if (!ep) {
          if (active) {
            setLoading(false);
            setError("Failed to load sales history");
          }
          return;
        }
        const fd = new FormData();
        fd.append("encrypted_payload", ep);
        payload = fd;
      } else {
        const params = new URLSearchParams();
        params.append("draw", String(page + 1));
        params.append("start", String(page * rowsPerPage));
        params.append("length", String(rowsPerPage));
        params.append("searchValue", searchDebounced);
        params.append("search[value]", searchDebounced);
        params.append("search[regex]", "false");
        params.append("searchByFromdate", startDate);
        params.append("searchByTodate", endDate);
        params.append("authorization", `Bearer ${token}`);
        params.append("order[0][column]", String(orderColumnIndex));
        params.append("order[0][dir]", "desc");
        columns.forEach((column, index) => {
          params.append(`columns[${index}][data]`, column.id);
          params.append(`columns[${index}][name]`, "");
          params.append(`columns[${index}][searchable]`, "true");
          params.append(`columns[${index}][orderable]`, "true");
          params.append(`columns[${index}][search][value]`, "");
          params.append(`columns[${index}][search][regex]`, "false");
        });
        payload = params;
        reqConfig.headers["Content-Type"] = "application/x-www-form-urlencoded";
      }
      axios
        .post(`${config.API_URL}/order/history`, payload, reqConfig)
        .then((response) => {
          if (!active) {
            return;
          }
          const json = normalizeEncryptedApiResponse(response.data, useEncrypt) || response.data || {};
          const rows = Array.isArray(json.data) ? json.data : [];
          setData(rows);
          const filtered = Number(json.recordsFiltered);
          const total = Number.isFinite(filtered) ? filtered : Number(json.recordsTotal) || rows.length;
          setTotalRows(total);
          setGrandTotal(json.total_grand_total ?? "");
          setCurrentGrandTotal(json.total_grand_total1 ?? "");
          if (rows.length === 0 && total > 0 && page > 0 && page * rowsPerPage >= total) {
            setPage(0);
          }
        })
        .catch((err) => {
          if (!active) {
            return;
          }
          if (err.name === "CanceledError") {
            return;
          }
          setError(err.response?.data?.message || err.message || "Failed to load sales history");
          setData([]);
          setTotalRows(0);
          setGrandTotal("");
          setCurrentGrandTotal("");
        })
        .finally(() => {
          if (!active) {
            return;
          }
          setLoading(false);
        });
    })();
    return () => {
      active = false;
      controller.abort();
    };
  }, [accessToken, token, page, rowsPerPage, startDate, endDate, searchDebounced]);

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
    setPage(0);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const handleDateChange = (value) => {
    const range = Array.isArray(value) ? value : [null, null];
    setSalesDates(range);
    const start = range[0] ? format(range[0], "MM/dd/yyyy") : "";
    const end = range[1] ? format(range[1], "MM/dd/yyyy") : "";
    setStartDate(start);
    setEndDate(end);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilter = () => {
    setSalesDates([null, null]);
    setStartDate("");
    setEndDate("");
    setSearchValue("");
    setSearchDebounced("");
    setPage(0);
  };

  const renderCell = (columnId, row) => {
    if (columnId === "reference_no") {
      const href = `${config.BASE_URL}/order_summary/print/${row.id}`;
      return (
        <MuiLink
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "inherit",
            textDecoration: "underline !important",
            textDecorationColor: "currentColor",
            textUnderlineOffset: "2px",
            "&:hover": {
              textDecoration: "underline !important",
              textDecorationColor: "currentColor"
            }
          }}
        >
          {row.reference_no}
        </MuiLink>
      );
    }
    if (columnId === "customer") {
      return (
        <Typography
          title={row.customer || ""}
          sx={{
            maxWidth: 220,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: "0.75rem",
            color: "#212b36"
          }}
        >
          {row.customer}
        </Typography>
      );
    }
    
    if (columnId === "sale_status") {
      const { color } = getStatusChipProps(row.sale_status);
      return (
        <Chip
          label={row.sale_status}
          color={color}
          variant="soft"
          size="small"
          sx={{ fontWeight: 500, fontSize: "0.75rem", px: 1.5, height: 24 }}
        />
      );
    }
    return row[columnId] ?? "";
  };

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={1} alignItems="center" justifyContent="space-between">
              <Grid2 size={{ xs: 12, md:2}} alignItems="center" sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                <Typography className="page-title">
                  Order History
                </Typography>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 4}} alignItems="center" sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 1 }}>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Typography component="span" sx={{ fontSize: "0.875rem", color: "#212b36", fontWeight: 600 }}>
                    Order Total:
                  </Typography>
                  <Typography component="span" sx={{ fontSize: "0.875rem", color: "#212b36", fontWeight: 400 }}>
                    {grandTotal}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Typography component="span" sx={{ fontSize: "0.875rem", color: "#212b36", fontWeight: 600 }}>
                    Current Total:
                  </Typography>
                  <Typography component="span" sx={{ fontSize: "0.875rem", color: "#212b36", fontWeight: 400 }}>
                    {currentGrandTotal}
                  </Typography>
                </Box>
                </Grid2>

              <Grid2 size={{ xs: 12, md: 3}}>
                <TextField
                  fullWidth
                  className="input-border"
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  size="small"
                  placeholder="Search orders..."
                  variant="outlined"
                  inputProps={{ maxLength: 10 }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline"/>
                        </InputAdornment>
                      )
                    }
                  }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, md: 3 }} sx={{
                display: "flex",
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: { xs: 'center', md: 'flex-end' },
                alignItems: { xs: 'flex-start', md: 'flex-end' },
                gap: 1,
              }}>

                <Box sx={{ width: { xs: '100%', md: '300px' } }}>
                  <CommonDatePicker className="input-border" value={salesDates} onChange={handleDateChange} />
                </Box>

                <Button
                  variant="outlined"
                  color="inherit"
                  className="button-variant-outlined"
                  startIcon={<Iconify icon="eva:refresh-outline" width={18} height={18} />}
                  onClick={handleClearFilter}
                  sx={{
                    width: { xs: '100%', md: 'auto' }
                  }}
                >
                  Clear
                </Button>
              </Grid2>
            </Grid2>
          </Box>
          <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                tableLayout: "fixed",
              }}
              className="table-cell-border"
            >
              <colgroup>
                {columns.map((column) => (
                  <col key={column.id} style={{ width: column.width ? `${column.width}px` : "auto" }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow
                  className="table-cell-header"
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.numeric ? "right" : "left"}
                      sx={{
                        width: column.width ? `${column.width}px` : "auto"
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? Array.from({ length: Math.min(rowsPerPage, 10) }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      {columns.map((column) => (
                        <TableCell
                          key={`${column.id}-${index}`}
                          align={column.numeric ? "right" : "left"}
                          sx={{
                            py: 1,
                            fontSize: "0.75rem",
                            width: column.width ? `${column.width}px` : "auto"
                          }}
                        >
                          <Skeleton variant="text" width="60%" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                  : data.length > 0
                    ? data.map((row, rowIndex) => (
                      <TableRow
                        key={row.id || row.reference_no || rowIndex}
                        hover
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(145, 158, 171, 0.04)"
                          },

                          transition: "background-color 0.2s ease"
                        }}
                      >
                        {columns.map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.numeric ? "right" : "left"}
                            className="table-cell"
                            sx={{
                              width: column.width ? `${column.width}px` : "auto",
                              ...(column.numeric && { fontWeight: 500 })
                            }}
                          >
                            {renderCell(column.id, row)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                    : (
                      <TableRow>
                        <TableCell colSpan={columns.length} sx={{ py: 8 }}>
                          <Typography align="center">
                            No records found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Section */}
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              className="pagination-styles"
            />
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default SalesOrderHistory;
